import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Award, CheckCircle, Clock, Heart, Home, MapPin,
  Phone, Shield, Star, Users,
} from 'lucide-react';
import { APP_CONFIG, ROUTES } from '../../config/app.config';

const values = [
  { icon: Shield, title: 'Integrity First', text: 'Clear recommendations, honest pricing, and no pressure. We treat every roof as if it protected our own family.' },
  { icon: Award, title: 'Craftsmanship', text: 'Our trained crews follow manufacturer specifications and proven installation standards on every project.' },
  { icon: Heart, title: 'People Matter', text: 'We build lasting relationships with homeowners, team members, suppliers, and the communities we serve.' },
  { icon: Clock, title: 'Dependable Service', text: 'We communicate clearly, arrive when promised, protect your property, and leave every jobsite clean.' },
];

// PLACEHOLDER PEOPLE — these are invented names paired with stock headshots.
// They must be replaced with Cates' real team and real photographs before launch;
// presenting fictional staff as real employees is misrepresentation, not filler.
const team = [
  { name: 'Team Member', role: 'Founder & President', experience: 'Replace with real bio', image: '' },
  { name: 'Team Member', role: 'Operations Director', experience: 'Replace with real bio', image: '' },
  { name: 'Team Member', role: 'Senior Project Manager', experience: 'Replace with real bio', image: '' },
];

const milestones = [
  { year: '1926', title: 'The Family Business Legacy Begins', text: 'Our family began owning and operating businesses in the community with a commitment to honest service and long-term relationships.' },
  { year: '1968', title: 'The Next Generation', text: 'A new generation carried forward the family values of hard work, integrity, and dependable service.' },
  { year: '2003', title: 'Four Generations of Community Roots', text: 'The family continued building and operating local businesses while remaining invested in the community it calls home.' },
  { year: 'Today', title: 'Cates Roofing & Contracting', text: 'The family name is now on roofs across the region — installed by our own W-2 crews, with premium Owens Corning systems and the long-term values shaped by four generations in business.' },
];

const AboutPage: React.FC = () => (
  <div>
    <section className="page-header" style={{ padding: '9rem 1.5rem 5rem', textAlign: 'center' }}>
      <div className="container-xl">
        <span style={{ color: '#1493e6', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          Fourth-Generation Family Legacy Since {APP_CONFIG.familyBusinessLegacySince}
        </span>
        <h1 style={{ color: 'white', fontSize: 'clamp(2.25rem, 5vw, 4rem)', fontWeight: 900, margin: '0.75rem 0 1rem', textTransform: 'uppercase' }}>
          Built the Way We'd Build Our Own
        </h1>
        <p style={{ color: '#e6edf5', maxWidth: '760px', margin: '0 auto', fontSize: '1.1rem', lineHeight: 1.75 }}>
          We started Cates Roofing with one goal: build roofs the way we'd build them for our own
          families. Everything below is a consequence of that decision — and every one of them
          exists because it changes what you get.
        </p>
      </div>
    </section>

    <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
      <div className="container-xl" style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: '4rem', alignItems: 'center' }}>
        <div className="reveal-left">
          <span style={{ color: '#1493e6', fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Our Story</span>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.8rem, 3vw, 2.65rem)', fontWeight: 900, margin: '0.6rem 0 1.25rem', textTransform: 'uppercase' }}>
            Why We Do It This Way
          </h2>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.25rem' }}>
            <strong style={{ color: 'var(--text-primary)' }}>Why we don't use subcontractors.</strong> Most roofing companies sell you a roof and then hand the work to whichever crew was available that week. You never meet them, and neither did the salesperson. Our crews are Cates employees on our payroll — so when we say the work will be done a certain way, there is somebody accountable for doing it that way.
          </p>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.25rem' }}>
            <strong style={{ color: 'var(--text-primary)' }}>Why we chose Owens Corning.</strong> Their certification is not a badge you buy — it requires licensing and insurance in good standing, a documented workmanship standard, and a satisfaction record the manufacturer keeps reviewing. It is also what keeps manufacturer cover enforceable, because a warranty protects a defective shingle, not a shingle installed incorrectly. Most roofing claims are lost on installation, not materials.
          </p>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.25rem' }}>
            <strong style={{ color: 'var(--text-primary)' }}>Why craftsmanship matters.</strong> Almost everything that determines whether a roof lasts is invisible from the ground — flashing, ventilation balance, nail placement, valley detail. It is exactly the set of things easiest to skip and hardest for a homeowner to check. So we take fewer roofs per season and give each one the hours it needs.
          </p>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
            <strong style={{ color: 'var(--text-primary)' }}>Why insurance guidance matters.</strong> A storm claim is decided by documentation most homeowners have never been asked to produce before. We inspect, photograph and write it up in the form adjusters expect, and we are on the roof when yours arrives — so a legitimate claim is not quietly reduced in the field.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.8rem' }}>
            {['Licensed, bonded & insured', '10-year workmanship warranty', 'In-house W-2 crews — no subcontractors', 'Free inspections & detailed estimates'].map(item => (
              <div key={item} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--text-primary)', fontSize: '0.88rem', fontWeight: 600 }}>
                <CheckCircle size={17} color="#1493e6" /> {item}
              </div>
            ))}
          </div>
        </div>
        <div className="reveal-right" style={{ position: 'relative', minHeight: '480px' }}>
          <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=900&auto=format&fit=crop&q=85" alt="Cates Roofing professionals working on a project" style={{ width: '88%', height: '430px', objectFit: 'cover', borderRadius: '1.25rem', marginLeft: 'auto', boxShadow: '0 24px 55px var(--shadow-color)' }} />
          <div style={{ position: 'absolute', left: 0, bottom: 0, background: '#0072c6', color: 'white', borderRadius: '1rem', padding: '1.4rem 1.6rem', boxShadow: '0 18px 36px rgba(0,114,198,0.28)' }}>
            <div style={{ fontSize: '1.65rem', fontWeight: 900, lineHeight: 1 }}>Since 1926</div>
            <div style={{ fontSize: '0.76rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.4rem' }}>Family Business Legacy</div>
          </div>
        </div>
      </div>
    </section>

    <section style={{ padding: '3rem 1.5rem', background: '#0072c6' }}>
      <div className="container-xl" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', textAlign: 'center' }}>
        {[
          { value: '3,200+', label: 'Roofs Completed' },
          { value: '2,900+', label: 'Happy Customers' },
          { value: '5.0', label: 'Google Rating' },
          { value: '24/7', label: 'Emergency Response' },
        ].map(item => (
          <div key={item.label}>
            <div style={{ color: 'white', fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', fontWeight: 900 }}>{item.value}</div>
            <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.76rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{item.label}</div>
          </div>
        ))}
      </div>
    </section>

    <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
      <div className="container-xl">
        <div style={{ textAlign: 'center', maxWidth: '650px', margin: '0 auto 3rem' }} className="reveal">
          <span style={{ color: '#1493e6', fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>What Guides Us</span>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, margin: '0.6rem 0', textTransform: 'uppercase' }}>Our Values</h2>
          <p style={{ color: 'var(--text-muted)' }}>The standards behind every recommendation, repair, and replacement.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '1.25rem' }}>
          {values.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className={`card reveal-scale reveal-delay-${i + 1}`} style={{ textAlign: 'center' }}>
              <div style={{ width: '3.5rem', height: '3.5rem', margin: '0 auto 1.1rem', borderRadius: '0.9rem', background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={25} color="#1493e6" />
              </div>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 800, marginBottom: '0.65rem', textTransform: 'uppercase' }}>{title}</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.86rem', lineHeight: 1.7, margin: 0 }}>{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
      <div className="container-xl">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <span style={{ color: '#1493e6', fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>How We Grew</span>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, marginTop: '0.6rem', textTransform: 'uppercase' }}>Our Journey</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
          {milestones.map(item => (
            <div key={item.year} className="card" style={{ borderTop: '4px solid #1493e6' }}>
              <div style={{ color: '#1493e6', fontSize: '1.65rem', fontWeight: 900 }}>{item.year}</div>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '0.98rem', fontWeight: 800, margin: '0.5rem 0' }}>{item.title}</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.84rem', lineHeight: 1.65, margin: 0 }}>{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
      <div className="container-xl">
        <div style={{ textAlign: 'center', maxWidth: '620px', margin: '0 auto 3rem' }}>
          <span style={{ color: '#1493e6', fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Meet the Team</span>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, margin: '0.6rem 0', textTransform: 'uppercase' }}>Roofing Professionals Who Care</h2>
          <p style={{ color: 'var(--text-muted)' }}>Experienced leadership, trained crews, and one shared commitment to excellent service.</p>
          <p style={{ color: '#1493e6', fontSize: '0.82rem', fontWeight: 700, marginTop: '0.75rem' }}>
            Awaiting Cates' real team photographs and bios.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', maxWidth: '950px', margin: '0 auto' }}>
          {team.map((member, i) => (
            <div key={`${member.role}-${i}`} className="card card-hover" style={{ padding: 0, overflow: 'hidden' }}>
              {member.image ? (
                <img src={member.image} alt={member.name} style={{ width: '100%', height: '270px', objectFit: 'cover', objectPosition: 'center top' }} />
              ) : (
                <div style={{
                  width: '100%', height: '270px',
                  background: 'linear-gradient(135deg, #050608 0%, #0072c6 100%)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                }}>
                  <Users size={30} color="rgba(255,255,255,0.85)" strokeWidth={1.6} />
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Photo coming soon</span>
                </div>
              )}
              <div style={{ padding: '1.25rem' }}>
                <h3 style={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: '1.05rem', margin: 0 }}>{member.name}</h3>
                <div style={{ color: '#1493e6', fontSize: '0.8rem', fontWeight: 700, margin: '0.3rem 0' }}>{member.role}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{member.experience}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
      <div className="container-xl" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }}>
        <div>
          <span style={{ color: '#1493e6', fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Credentials & Community</span>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 900, margin: '0.6rem 0 1rem', textTransform: 'uppercase' }}>Qualified to Protect What Matters</h2>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>Our certifications represent ongoing training, proven installation quality, and strong customer satisfaction—not just a logo on a truck.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', marginTop: '1.25rem' }}>
            {['Owens Corning Preferred Contractor — factory-certified installation', 'Locally and family owned — not a corporate chain', 'In-house W-2 crews, never subcontracted'].map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}><Star size={17} color="#1493e6" fill="#1493e6" /> {item}</div>
            ))}
          </div>
        </div>
        <div className="card" style={{ padding: '2rem' }}>
          <MapPin size={30} color="#1493e6" />
          <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', fontWeight: 800, margin: '1rem 0 0.65rem' }}>Proudly Serving Northeast Florida</h3>
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, fontSize: '0.9rem' }}>Based in Jacksonville and serving Ponte Vedra, Orange Park, St. Augustine, Fernandina Beach, Middleburg, Green Cove Springs, Nocatee, Atlantic Beach, Neptune Beach, Fleming Island, Jacksonville Beach, and nearby communities.</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', color: 'var(--text-secondary)', marginTop: '1.2rem', fontSize: '0.88rem' }}><Home size={17} color="#1493e6" /> {APP_CONFIG.address}, {APP_CONFIG.city}, {APP_CONFIG.state} {APP_CONFIG.zip}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', color: 'var(--text-secondary)', marginTop: '0.75rem', fontSize: '0.88rem' }}><Users size={17} color="#1493e6" /> Locally owned and operated</div>
        </div>
      </div>
    </section>

    <section style={{ padding: '5rem 1.5rem', background: 'linear-gradient(135deg, #050608, #0072c6)', textAlign: 'center' }}>
      <div className="container-xl" style={{ maxWidth: '720px' }}>
        <h2 style={{ color: 'white', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 900, marginBottom: '0.8rem', textTransform: 'uppercase' }}>Ready to Work With a Roofer You Can Trust?</h2>
        <p style={{ color: '#e6edf5', marginBottom: '2rem' }}>Tell us what your roof needs. Our team will provide a clear inspection, practical options, and a free estimate.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
          <Link to={ROUTES.QUOTE} className="btn-primary">Get a Free Estimate <ArrowRight size={16} /></Link>
          <a href={`tel:${APP_CONFIG.phoneRaw}`} className="btn-secondary" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.65)' }}><Phone size={16} /> {APP_CONFIG.phone}</a>
        </div>
      </div>
    </section>

    <style>{`
      @media(max-width: 900px) {
        section .container-xl[style*="grid-template-columns: 1.05fr 0.95fr"],
        section .container-xl[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
      }
      @media(max-width: 640px) {
        section .container-xl[style*="grid-template-columns: repeat(4, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
      }
    `}</style>
  </div>
);

export default AboutPage;
